import { Alert, Chip, Link, Table, TableBody, TableCell, TableHead, TableRow, Tooltip } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import dayjs from "dayjs";
import type { CheckStatus, CheckSummary, Column, ColumnType, ReportRow, ReviewStatus } from "@/api/closeReview";

const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const number = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 });

export function formatValue(value: unknown, type: ColumnType): string {
  if (value === null || value === undefined || value === "") return "";
  if (type === "money" && typeof value === "number") {
    const v = Math.round(value) || 0; // no "-$0"
    return v < 0 ? `(${money.format(-v)})` : money.format(v);
  }
  if (type === "number" && typeof value === "number") return number.format(value);
  if (type === "percent" && typeof value === "number") return `${(value * 100).toFixed(1)}%`;
  if (type === "date") return dayjs(String(value)).format("MMM D, YYYY");
  return String(value);
}

export const numeric = (type: ColumnType) => ["money", "number", "percent"].includes(type);

// Per-cell NetSuite drilldowns that fsp-bi-hub sends as values._links {column key: url}.
export function rowLinks(values: Record<string, unknown>): Record<string, string> {
  const links = values._links;
  return links && typeof links === "object" ? (links as Record<string, string>) : {};
}

// One table cell's content. Zero amounts are a muted dash so the real numbers stand out; a cell with a
// NetSuite link opens it in a new tab without triggering the row's own click.
export function Cell({ value, type, href }: { value: unknown; type: ColumnType; href?: string }) {
  if (type === "link" && value) href = String(value);
  // Money rounds to whole dollars, so under 50 cents is a zero; counts and percents only at exactly zero.
  const zero = typeof value === "number" && (type === "money" ? Math.abs(value) < 0.5 : numeric(type) && value === 0);
  const text = type === "link" ? "Open" : zero ? "–" : formatValue(value, type);
  if (href && !zero && text) {
    return (
      <Tooltip title="Open in NetSuite" enterDelay={600}>
        <Link href={href} target="_blank" rel="noopener noreferrer" underline="hover" onClick={(e) => e.stopPropagation()}
          sx={{ color: "info.dark", fontWeight: 500, display: "inline-flex", alignItems: "center", gap: 0.5, "& svg": { opacity: 0 }, "&:hover svg": { opacity: 0.6 } }}>
          {text}
          <OpenInNewIcon sx={{ fontSize: 12 }} />
        </Link>
      </Tooltip>
    );
  }
  return <span style={zero ? { color: "rgba(0,0,0,0.3)" } : undefined}>{text}</span>;
}

const STATUS_COLOR: Record<CheckStatus, "success" | "warning" | "info" | "error"> = {
  pass: "success", flag: "warning", info: "info", error: "error",
};
const STATUS_LABEL: Record<CheckStatus, string> = { pass: "Pass", flag: "Flag", info: "Info", error: "Error" };
export const STATUS_ORDER: Record<CheckStatus, number> = { error: 0, flag: 1, info: 2, pass: 3 };

export function StatusChip({ status }: { status: CheckStatus }) {
  return <Chip size="small" color={STATUS_COLOR[status]} label={STATUS_LABEL[status]} sx={{ minWidth: 52 }} />;
}

const REVIEW_LABEL: Record<ReviewStatus, string> = {
  open: "Open", acknowledged: "Acknowledged", resolved: "Resolved", waived: "Waived",
};

export function ReviewChip({ status }: { status: ReviewStatus }) {
  return (
    <Chip size="small" variant={status === "open" ? "outlined" : "filled"} color={status === "open" ? "default" : "primary"}
      label={REVIEW_LABEL[status]} />
  );
}

// A flag or error nobody has reviewed yet.
export const needsReview = (c: Pick<CheckSummary, "status" | "review_status" | "changed_since_review">) =>
  ((c.status === "flag" || c.status === "error") && c.review_status === "open") || c.changed_since_review;

// What the review column says: nothing for a pass or info nobody touched, "Needs review" for an
// open flag or error, else the review status.
export function ReviewState({ check }: { check: CheckSummary }) {
  if (check.changed_since_review) {
    return <Tooltip title="The result changed after it was reviewed"><Chip size="small" color="warning" variant="outlined" label="Changed since review" /></Tooltip>;
  }
  if (check.review_status !== "open") return <ReviewChip status={check.review_status} />;
  if (needsReview(check)) return <Chip size="small" color="warning" variant="outlined" label="Needs review" />;
  return null;
}

// A note for a part of the close that hasn't loaded yet, from CloseReview::Schedule.
export function NotYet({ note }: { note: string }) {
  return <Alert severity="info" variant="outlined">Not loaded yet. {note}</Alert>;
}

// A P&L / Balance Sheet / Statistical Journals table from fsp-bi-hub.
export function ReportTable({ columns, rows }: { columns: Column[]; rows: ReportRow[] }) {
  return (
    <Table size="small" stickyHeader>
      <TableHead>
        <TableRow>
          {columns.map((c) => (
            <TableCell key={c.key} align={numeric(c.type) ? "right" : "left"} sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
              {c.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r) => {
          const links = rowLinks(r.values);
          return (
            <TableRow key={r.key} hover={r.style === "data"}
              sx={{ bgcolor: r.style === "section" ? "action.hover" : undefined, "& td": r.style === "total" ? { borderTop: 1, borderColor: "divider" } : undefined }}>
              {columns.map((c, i) => {
                const type = i > 0 && r.format && numeric(c.type) ? r.format : c.type;
                return (
                  <TableCell key={c.key} align={numeric(c.type) ? "right" : "left"}
                    sx={{ fontWeight: r.style === "data" ? 400 : 600, whiteSpace: "pre", py: 0.5, fontVariantNumeric: "tabular-nums" }}>
                    <Cell value={r.values[c.key]} type={type} href={links[c.key]} />
                  </TableCell>
                );
              })}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
